import { Box, Stack, Typography, Paper, Chip } from '@mui/material';
import { useNavigate } from 'react-router';

import HomeIcon from '@/assets/icons/home.svg?react';
import IssueIcon from '@/assets/icons/issue-exclamation.svg?react';
import { usePropertiesContext } from '@/context/PropertiesProvider';
import { useUploaderContext } from '@/context/UploaderProvider';
import { useTranslationPrefix } from '@/translations/i18n';

export const CompletionView = () => {
  const navigate = useNavigate();
  const t = useTranslationPrefix('uploader');
  const { streamingProperty, streamingIssuesCount, resetProcessing, lastProcessedPropertyId } =
    useUploaderContext();
  const { properties } = usePropertiesContext();

  // Count issues by severity
  const criticalCount = streamingIssuesCount?.critical || 0;
  const moderateCount = streamingIssuesCount?.moderate || 0;
  const urgentCount = streamingIssuesCount?.urgent || 0;
  const lowCount = streamingIssuesCount?.low || 0;

  // Shared chip style
  const chipBaseStyle = {
    display: 'flex',
    color: 'var(--tx-text)',
    height: '25px',
    width: '170px',
    padding: '8px 20px',
    justifyContent: 'center',
    alignItems: 'center',
    gap: '4px',
    flexShrink: 0,
    borderRadius: '10px',
    textAlign: 'center',
    fontFamily: 'Manrope',
    fontSize: '14px',
    fontStyle: 'normal',
    fontWeight: 500,
    lineHeight: '14px',
  };

  // Severity-specific styles
  const severityStyles = {
    critical: { ...chipBaseStyle, bgcolor: '#B05670' },
    moderate: { ...chipBaseStyle, bgcolor: '#EFC46C' },
    urgent: { ...chipBaseStyle, bgcolor: '#E88F67' },
    low: { ...chipBaseStyle, bgcolor: '#7ABFB8' },
  };

  return (
    <Stack width="100%" alignItems="center">
      {/* Success Icon - Green Circle with Checkmark */}

      {/* All Set Heading */}
      <Typography variant="h4" fontWeight="600" lineHeight={1.35} sx={{ mb: 2 }}>
        {t('all_set')}
      </Typography>

      {/* Subtitle */}
      <Typography
        textAlign="center"
        color="text.secondary"
        variant="h6"
        fontWeight="regular"
        lineHeight={1.35}>
        {t('dashboard_built')}
      </Typography>

      {/* Property Info Card */}
      <Paper
        sx={{
          width: '370px',
          bgcolor: 'background.paper',
          p: 3,
          borderRadius: 2,
          color: 'text.secondary',
          paddingTop: 8,
        }}>
        {/* Property Address Section */}
        <Stack spacing={1} mb={4}>
          <Stack direction="row" alignItems="center" spacing={1}>
            <HomeIcon fontSize="small" />
            <Typography variant="body2">{t('property_address')}</Typography>
          </Stack>
          <Typography variant="h6" color="text.primary">
            {streamingProperty || t('no_property')}
          </Typography>
        </Stack>

        {/* Issues Found Section */}
        <Stack spacing={1} pt={2}>
          <Stack direction="row" alignItems="center" spacing={1}>
            <IssueIcon fontSize="small" />
            <Typography variant="body2">{t('issues_found')}</Typography>
          </Stack>

          {/* Issue Severity Chips - 2x2 Grid */}
          <Stack spacing={2} pt={2}>
            {/* Top Row: Critical and Moderate */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2 }}>
              <Chip label={`${t('critical')}: ${criticalCount}`} sx={severityStyles.critical} />
              <Chip label={`${t('urgent')}: ${urgentCount}`} sx={severityStyles.urgent} />
            </Box>

            {/* Bottom Row: Urgent and Low */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2 }}>
              <Chip label={`${t('moderate')}: ${moderateCount}`} sx={severityStyles.moderate} />
              <Chip label={`${t('low')}: ${lowCount}`} sx={severityStyles.low} />
            </Box>
          </Stack>
        </Stack>
      </Paper>

      {/* Dashboard Button */}
      <Box
        component="button"
        sx={{
          width: '350px',
          height: '50px',
          borderRadius: '10px',
          background: 'var(--tx-fill-2)',
          border: 'none',
          color: 'var(--tx-text)',
          textAlign: 'center',
          fontFamily: 'Manrope',
          fontSize: '18px',
          fontStyle: 'normal',
          fontWeight: 400,
          lineHeight: 'normal',
          cursor: 'pointer',
          transition: 'opacity 0.2s ease',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          marginTop: '25px', // Push to bottom of flex container
          marginBottom: '5px', // 15px padding from bottom
          flexShrink: 0,
          '&:hover': {
            opacity: 1,
          },
        }}
        onClick={() => {
          resetProcessing();
          if (lastProcessedPropertyId) {
            navigate('/', {
              state: {
                preselectedProperty:
                  properties?.filter(
                    (property) => property.property.id === lastProcessedPropertyId,
                  )[0] || null,
              },
            });
          } else {
            navigate('/');
          }
        }}>
        {t('take_me_to_dashboard')}
      </Box>
    </Stack>
  );
};
